import { createBrowserRouter, RouterProvider, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/core/contexts/AuthContext";
import { ProtectedRoute } from "@/core/components/ProtectedRoute";
import { AppLayout } from "@/layouts/AppLayout";
import { Login } from "@/core/pages/Login";
import { CadastroPublico } from "@/core/pages/CadastroPublico";
import { EsqueciSenha } from "@/core/pages/EsqueciSenha";
import { RedefinirSenha } from "@/core/pages/RedefinirSenha";
import { HomeRedirect } from "@/core/pages/HomeRedirect";
import { Inicio } from "@/core/pages/Inicio";
import { Modulos } from "@/core/pages/Modulos";
import { Nucleo } from "@/core/pages/Nucleo";
import { DashboardAdmin } from "@/core/pages/DashboardAdmin";
import { DashboardUsuario } from "@/modules/biblioteca/pages/DashboardUsuario";
import { GerenciarUsuarios } from "@/core/pages/GerenciarUsuarios";
import { GerenciarMinisterios } from "@/core/pages/GerenciarMinisterios";
import { MinisterioDetalhe } from "@/core/pages/MinisterioDetalhe";
import { GerenciarModulos } from "@/core/pages/GerenciarModulos";
import { ConfiguracoesIgreja } from "@/core/pages/ConfiguracoesIgreja";
import { GerenciarLivros } from "@/modules/biblioteca/pages/GerenciarLivros";
import { GerenciarCategorias } from "@/modules/biblioteca/pages/GerenciarCategorias";
import { GerenciarEmprestimos } from "@/modules/biblioteca/pages/GerenciarEmprestimos";
import { Calendario } from "@/modules/calendario/pages/Calendario";
import { GerenciarEventos } from "@/modules/eventos/pages/GerenciarEventos";
import { GerenciarReservas } from "@/modules/reservas/pages/GerenciarReservas";
import { GerenciarAmbientes } from "@/modules/reservas/pages/GerenciarAmbientes";
import { ListaEscalasPage } from "@/modules/escala/pages/ListaEscalasPage";
import { DetalheEscalaPage } from "@/modules/escala/pages/DetalheEscalaPage";
import { NovaEscalaPage } from "@/modules/escala/pages/NovaEscalaPage";
import { ListaComunicadosPage } from "@/modules/comunicados/pages/ListaComunicadosPage";
import { NovoComunicadoPage } from "@/modules/comunicados/pages/NovoComunicadoPage";
import { EditarComunicadoPage } from "@/modules/comunicados/pages/EditarComunicadoPage";
import { ListaVisitantesPage } from "@/modules/visitantes/pages/ListaVisitantesPage";
import { FichaVisitantePage } from "@/modules/visitantes/pages/FichaVisitantePage";
import { NovoVisitantePage } from "@/modules/visitantes/pages/NovoVisitantePage";
import { EditarVisitantePage } from "@/modules/visitantes/pages/EditarVisitantePage";
import { InformarVisitantePage } from "@/modules/visitantes/pages/InformarVisitantePage";
import { EditarInformadoPage } from "@/modules/visitantes/pages/EditarInformadoPage";
import { AvaliarInformadoPage } from "@/modules/visitantes/pages/AvaliarInformadoPage";
import { SegurancaConta } from "@/core/pages/SegurancaConta";
import { NotificacoesConfiguracao } from "@/core/pages/NotificacoesConfiguracao";
import { MeuPerfil } from "@/core/pages/MeuPerfil";
import { AjudaLista } from "@/core/pages/AjudaLista";
import { AjudaApresentacao } from "@/core/pages/AjudaApresentacao";
import { AjudaLicao } from "@/core/pages/AjudaLicao";
import { AjudaConcluida } from "@/core/pages/AjudaConcluida";

/**
 * Cada área de acesso (DA-002) tem seu próprio guard envolvendo um AppLayout,
 * então o shell (cabeçalho + menu) só renderiza DEPOIS que o guard aprova —
 * sem "flash" de header em telas não autorizadas. As rotas de auth e o
 * redirect "/" ficam FORA do shell.
 */
function ConteudoRotas() {
  return (
    <>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro" element={<CadastroPublico />} />
          <Route path="/cadastro/:codigo" element={<CadastroPublico />} />
          <Route path="/esqueci-senha" element={<EsqueciSenha />} />
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />

          {/* Redirect puro, sem o shell. */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<HomeRedirect />} />
          </Route>

          {/* Home unificada (preview) — qualquer usuário autenticado. */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/inicio" element={<Inicio />} />
              {/* Sprint 3 — barra fixa inferior: aba "Módulos", lista os módulos
                  ativos/visíveis (mesmo conteúdo que antes vivia na Início). */}
              <Route path="/modulos" element={<Modulos />} />
              {/* Calendário (agregador) é universal — sempre visível a qualquer
                  autenticado, não é um módulo de modulos_visiveis(). */}
              <Route path="/calendario" element={<Calendario />} />
              {/*
               * Ministério visto pelo LÍDER (DA-004): fora do guard de admin
               * de propósito — a página só renderiza a governança para líder
               * ativo ou admin, e a RLS é o portão real no banco.
               */}
              <Route path="/ministerios/:id" element={<MinisterioDetalhe />} />
              {/* Autoatendimento de verificação em duas etapas (MFA) — 29/07/2026.
                  Sem link em nenhum menu de propósito (F6-XX regra 2, invisibilidade
                  do Fantasma, ainda pendente de decisão de produto); qualquer pessoa
                  autenticada que souber a URL configura a PRÓPRIA conta aqui. */}
              <Route path="/conta/seguranca" element={<SegurancaConta />} />
              {/* Frente 2, Fase 3 — preferências de notificação e push. Sem link em
                  nenhum menu principal; acessível pelo ícone de engrenagem no
                  cabeçalho do painel do sino (NotificationsPanel). */}
              <Route path="/conta/notificacoes" element={<NotificacoesConfiguracao />} />
              {/* Meu perfil — aberto pelo item "Meu perfil" do menu Conta. */}
              <Route path="/perfil" element={<MeuPerfil />} />
              {/* Ajuda do Elos (v1, membro comum) — só orienta; não concede nem muda
                  permissão. Aberta pelo item "Ajuda do Elos" do menu Conta e pelo
                  cartão "Novo por aqui?" do Início. As rotas estáticas vêm antes
                  de ":licaoId" para ficar óbvio na leitura. */}
              <Route path="/ajuda" element={<AjudaLista />} />
              <Route path="/ajuda/apresentacao" element={<AjudaApresentacao />} />
              <Route path="/ajuda/concluida" element={<AjudaConcluida />} />
              <Route path="/ajuda/:licaoId" element={<AjudaLicao />} />
            </Route>
          </Route>

          {/* Eventos e Reservas (SEG-02, auditoria 20/07/2026): antes ficavam
              no bloco acima sem checagem de módulo — qualquer autenticado
              acessava a rota direto por URL mesmo com o módulo desativado ou
              sem visibilidade para a pessoa. Agora o guard exige o módulo em
              modulosVisiveis (RLS em paralelo trava a leitura/escrita real —
              migration 0032). Criação/edição continua gated pela RLS e pelos
              botões condicionais de cada tela. */}
          <Route element={<ProtectedRoute moduloRequerido="eventos" />}>
            <Route element={<AppLayout />}>
              <Route path="/eventos" element={<GerenciarEventos />} />
            </Route>
          </Route>
          <Route element={<ProtectedRoute moduloRequerido="reservas" />}>
            <Route element={<AppLayout />}>
              <Route path="/reservas" element={<GerenciarReservas />} />
            </Route>
          </Route>

          {/* Fase 3 (frontend definitivo, 27/09/2026) — Escala. Autorização de
              escrita é POR MINISTÉRIO (lidera_ou_gerencia_ministerio), nunca um
              flag global — por isso, ao contrário de Eventos/Reservas, não há
              (nem deve haver) um `podeGerenciarEscala` em ProtectedRoute: o
              guard aqui só verifica visibilidade do módulo (`modulosVisiveis`);
              cada tela decide o que pode fazer a partir da RLS/RPCs reais. */}
          <Route element={<ProtectedRoute moduloRequerido="escala" />}>
            <Route element={<AppLayout />}>
              <Route path="/escalas" element={<ListaEscalasPage />} />
              {/* "nova" fica antes de ":id" — o React Router já dá preferência ao caminho estático, mas assim a leitura é óbvia. */}
              <Route path="/escalas/nova" element={<NovaEscalaPage />} />
              <Route path="/escalas/:id" element={<DetalheEscalaPage />} />
            </Route>
          </Route>

          {/* Comunicados — o guard só verifica a visibilidade do elo
              (`modulosVisiveis`); quem pode publicar/editar é decidido pelas
              RPCs e a tela esconde os botões de quem não gerencia. */}
          <Route element={<ProtectedRoute moduloRequerido="comunicados" />}>
            <Route element={<AppLayout />}>
              <Route path="/comunicados" element={<ListaComunicadosPage />} />
              <Route path="/comunicados/novo" element={<NovoComunicadoPage />} />
              <Route path="/comunicados/:id/editar" element={<EditarComunicadoPage />} />
            </Route>
          </Route>

          {/* Visitantes — o guard só verifica a visibilidade do elo
              (`modulosVisiveis`); o que cada papel vê e faz é decidido pelas RPCs
              (ministério responsável, líder de outro ministério, administrador). As rotas
              estáticas vêm antes das com `:id` para ficar óbvio na leitura. */}
          <Route element={<ProtectedRoute moduloRequerido="visitantes" />}>
            <Route element={<AppLayout />}>
              <Route path="/visitantes" element={<ListaVisitantesPage />} />
              <Route path="/visitantes/novo" element={<NovoVisitantePage />} />
              <Route path="/visitantes/informar" element={<InformarVisitantePage />} />
              <Route path="/visitantes/informados/:id/editar" element={<EditarInformadoPage />} />
              <Route path="/visitantes/informados/:id/avaliar" element={<AvaliarInformadoPage />} />
              <Route path="/visitantes/:id" element={<FichaVisitantePage />} />
              <Route path="/visitantes/:id/editar" element={<EditarVisitantePage />} />
            </Route>
          </Route>

          {/* Hub administrativo: qualquer um dos dois níveis do DA-002. */}
          <Route
            element={
              <ProtectedRoute niveisPermitidos={["admin-plataforma", "gerencia-biblioteca"]} />
            }
          >
            <Route element={<AppLayout />}>
              <Route path="/admin" element={<DashboardAdmin />} />
              <Route
                path="/admin/visualizar-como-usuario"
                element={<DashboardUsuario modoPreview />}
              />
            </Route>
          </Route>

          {/* Nível 1 (DA-002): Núcleo — pessoas, ministérios, acessos, módulos e igreja.
              Sprint 3: /nucleo é a aba da barra fixa (lista as 5 entradas abaixo). */}
          <Route element={<ProtectedRoute niveisPermitidos={["admin-plataforma"]} />}>
            <Route element={<AppLayout />}>
              <Route path="/nucleo" element={<Nucleo />} />
              <Route path="/admin/usuarios" element={<GerenciarUsuarios />} />
              <Route path="/admin/ministerios" element={<GerenciarMinisterios />} />
              <Route path="/admin/ministerios/:id" element={<MinisterioDetalhe />} />
              {/* A tela "Acessos" foi extinta (Skill v3.0): a gestão de quem
                  gerencia cada módulo mora dentro do Ministério responsável. */}
              <Route path="/admin/modulos" element={<GerenciarModulos />} />
              <Route path="/admin/igreja" element={<ConfiguracoesIgreja />} />
            </Route>
          </Route>

          {/* Nível 2 (DA-002): gestão de Ambientes (dentro do módulo Reservas). */}
          <Route
            element={<ProtectedRoute niveisPermitidos={["admin-plataforma", "gerencia-reservas"]} />}
          >
            <Route element={<AppLayout />}>
              <Route path="/reservas/ambientes" element={<GerenciarAmbientes />} />
            </Route>
          </Route>

          {/* Nível 2 (DA-002): módulo Biblioteca. Admin da plataforma (Nível 1) SEMPRE
              tem acesso, mesmo sem ser especificamente gestor do módulo — por isso
              "admin-plataforma" também entra aqui (bug: admin caía na Home ao abrir
              Acervo/Categorias/Empréstimos por essa rota exigir só "gerencia-biblioteca"). */}
          <Route
            element={
              <ProtectedRoute niveisPermitidos={["admin-plataforma", "gerencia-biblioteca"]} />
            }
          >
            <Route element={<AppLayout />}>
              <Route path="/admin/livros" element={<GerenciarLivros />} />
              <Route path="/admin/categorias" element={<GerenciarCategorias />} />
              <Route path="/admin/emprestimos" element={<GerenciarEmprestimos />} />
            </Route>
          </Route>

          {/* Área do membro. */}
          <Route element={<ProtectedRoute apenasSemAcessoAdministrativo />}>
            <Route element={<AppLayout />}>
              <Route path="/usuario" element={<DashboardUsuario />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </>
  );
}

/**
 * Roteador de dados (e não `<BrowserRouter>`): é o que permite `useBlocker`, usado pelas telas
 * de Criar/Editar para avisar "Sair sem salvar?" na seta ‹, no menu inferior e no voltar do
 * navegador. A rota curinga entrega todas as rotas abaixo exatamente como antes.
 */
const router = createBrowserRouter([{ path: "*", element: <ConteudoRotas /> }]);

export function AppRoutes() {
  return <RouterProvider router={router} />;
}
